"use client";

import { useRouter } from "next/navigation";
import type { VoteAccess } from "@/domain/votes/vote";
import { createContext, useContext, type ReactNode } from "react";
import { AccountMenu } from "./workspace/AccountMenu";

interface Session {
  name: string;
  email: string;
  isAdmin: boolean;
  /** The user's access to the community vote (Settings → Users; an administrator always votes). */
  voteAccess: VoteAccess;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

/**
 * The signed-in session for the app's controls (there is no top bar: the controls sit in the workspace's market-tab
 * row). There are no other pages — every tool lives in the workspace.
 */
export function AppShell({ children, name, email, isAdmin, voteAccess }: { children: ReactNode; name: string; email: string; isAdmin: boolean; voteAccess: VoteAccess }) {
  const router = useRouter();
  const signOut = async () => {
    await fetch("/api/auth/logout", { method: "POST" });
    router.push("/login");
    router.refresh();
  };
  return <SessionContext.Provider value={{ name, email, isAdmin, voteAccess, signOut }}>{children}</SessionContext.Provider>;
}

/** The signed-in user's access to the community vote (none without a session — the server checks each request anyway). */
export function useVoteAccess(): VoteAccess {
  return useContext(SessionContext)?.voteAccess ?? "off";
}

/**
 * The app's controls: the account menu (profile, settings, sign-out), centred on the market tabs beside it (its 28 px
 * button 2 px above the bar's foot, the tabs 32 px from it) and parted from them by a rule (on phones the tabs scroll up to it).
 */
export function AppControls() {
  const session = useContext(SessionContext);
  if (!session) return null;
  return <AccountMenu name={session.name} email={session.email} isAdmin={session.isAdmin} onSignOut={session.signOut} className="ml-auto shrink-0 border-l border-line pb-0.5 pl-2" />;
}
